/**
 * Central iconography for the clinical (Psychologist-flow) surface.
 *
 * Every clinical component imports icons from here. No clinical component
 * imports from `lucide-react` directly. This is the single place to swap an
 * icon, document intentional overlaps, and keep symbol vocabulary coherent.
 *
 * Deliberate overlaps (documented, not accidents):
 * - CheckCircle2    → caseReport.approved, session.completed,
 *                     assignment.scored, exercise.reviewed (shared "done")
 * - XCircle         → caseReport.rejected, session.cancelled (shared "ended
 *                     badly")
 * - Lock            → action.revealPhi (idle), state.forbidden (both are
 *                     access boundaries)
 * - AlertOctagon    → flagSeverity.crisis, nav.redFlags (nav advertises the
 *                     worst thing in the queue)
 * - ChevronsUp      → caseReportStatus.escalated_to_chief, action.escalate
 *                     (button and resulting chip share the symbol)
 * - CalendarClock   → sessionStatus.rescheduled, nav.availability
 *                     (both deal with time re-allocation)
 * - Inbox           → assignment.submitted, exercise.submitted,
 *                     nav.approvalQueue (awaiting-your-action metaphor)
 * - Send            → assignment.assigned, exercise.assigned
 *                     ("we dispatched this to the client")
 *
 * `*.in_progress` entries are null by design — the StatusChip renders a
 * 2-dot pulse for those states instead of an icon.
 */

import {
  AlertOctagon,
  AlertTriangle,
  Archive,
  Bell,
  Calendar,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronsUp,
  Circle,
  ClipboardList,
  Clock,
  Download,
  Dumbbell,
  FilePen,
  Inbox,
  LayoutDashboard,
  Library,
  Lock,
  MapPin,
  Paperclip,
  Pencil,
  Phone,
  PhoneCall,
  PhoneOff,
  Plus,
  ScrollText,
  Send,
  Settings,
  ShieldCheck,
  Trash2,
  UserX,
  UsersRound,
  Video,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

export const icons = {
  sessionMode: {
    online: Video,
    in_person: MapPin,
    helpline: Phone,
  },
  flagSeverity: {
    crisis: AlertOctagon,
    acute_risk: AlertTriangle,
    resolved: ShieldCheck,
    closed: Archive,
  },
  caseReportStatus: {
    draft: FilePen,
    pending_review: Clock,
    approved: CheckCircle2,
    rejected: XCircle,
    escalated_to_chief: ChevronsUp,
  },
  sessionStatus: {
    scheduled: Calendar,
    in_progress: null,
    completed: CheckCircle2,
    no_show: UserX,
    cancelled: XCircle,
    rescheduled: CalendarClock,
  },
  assignmentStatus: {
    assigned: Send,
    pending: Clock,
    in_progress: null,
    submitted: Inbox,
    completed: CheckCircle2,
    scored: CheckCircle2,
  },
  exerciseStatus: {
    assigned: Send,
    in_progress: null,
    submitted: Inbox,
    reviewed: CheckCircle2,
    // Client self-help vocab — pending shares the Send icon with the
    // staff `assigned` status; completed reuses Inbox like `submitted`.
    pending: Send,
    completed: Inbox,
  },
  action: {
    add: Plus,
    edit: Pencil,
    delete: Trash2,
    approve: Check,
    reject: X,
    escalate: ChevronsUp,
    revealPhi: Lock,
    attach: Paperclip,
    download: Download,
    startCall: PhoneCall,
    endCall: PhoneOff,
    record: Circle,
  },
  nav: {
    dashboard: LayoutDashboard,
    clients: UsersRound,
    sessions: Calendar,
    assessments: ClipboardList,
    redFlags: AlertOctagon,
    caseReports: ScrollText,
    approvalQueue: Inbox,
    exercises: Dumbbell,
    engagement: Library,
    availability: CalendarClock,
    notifications: Bell,
    settings: Settings,
  },
  state: {
    error: AlertTriangle,
    forbidden: Lock,
  },
} as const;

export type IconSlot = LucideIcon | null;
